import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, message, Modal } from 'antd';
import flat from 'flat';
import { ValidationError } from '@core/form';
import { useService } from '@core/inversify-react';
import { playSound } from '@core/utils';
import { ErrorAudioUrls } from '@core/constants';

import { useAuth } from '@modules/auth';
import { useBranches } from '@modules/branches';
import { useBoxes, useCloseBox, useMyBox, useSelectBox, useTransferBox } from '@modules/boxes';

import { GetDeclarationByTrackCodeRepoType, IGetDeclarationByTrackCodeRepo } from '../repos';
import { IBarcode } from '../interfaces';

export const useDeclarationsBoxAcceptance = () => {
  const boxes = useBoxes();
  const myBox = useMyBox({ refetchInterval: 10000 });
  const selectBoxMutation = useSelectBox();
  const closeBoxMutation = useCloseBox();
  const transferBoxMutation = useTransferBox();
  const getDeclarationByTrackCodeRepo = useService<IGetDeclarationByTrackCodeRepo>(GetDeclarationByTrackCodeRepoType);
  const branches = useBranches();
  const { user } = useAuth();

  const barcodeInputRef = useRef<Input>(null);
  const [disabled, setDisabled] = useState(false);
  const [isBoxSelectVisible, setIsBoxSelectVisible] = useState(false);
  const [barcodes, setBarcodes] = useState<IBarcode[]>([]);
  const [duplicatedTrackCodes, setDuplicatedTrackCodes] = useState<IBarcode[]>([]);
  const [type, setType] = useState<'acceptance' | 'transfer'>('acceptance');
  const [branchId, setBranchId] = useState<string | undefined>(user.data?.adminBranchId?.toString());
  const [boxId, setBoxId] = useState('');
  const [barcodeType, setBarcodeType] = useState(false);
  const [barcodeMap, setBarcodeMap] = useState<Record<string, boolean>>({});

  const tableData = useMemo(
    () =>
      barcodes.map((barcode, index) => ({
        id: index + 1,
        barcode: barcode.barcode,
        branch_name: barcode.branch_name,
        flight_name: barcode.flight_name,
        requires_declaration: !!barcodeMap[barcode.barcode],
      })),
    [barcodes, barcodeMap],
  );

  const isRequiredDeclarationExist = useMemo(() => tableData.some((row) => row.requires_declaration), [tableData]);

  const filteredBoxes = useMemo(() => boxes.data?.filter((box) => box.branch?.id.toString() === branchId), [boxes.data, branchId]);

  const onCloseBoxSelect = useCallback(() => {
    setIsBoxSelectVisible(false);
  }, []);

  const onOpenBoxSelect = useCallback(() => {
    setIsBoxSelectVisible(true);
  }, []);

  const closeBoxMutationError = useMemo(() => {
    if (closeBoxMutation.error instanceof ValidationError) {
      const errors = flat.unflatten(closeBoxMutation.error.errors);
      const message =
        errors.trackCode
          ?.map((error: any, index: number) => (error ? error.join(' ').replace(` seçilmiş track_code.${index}`, barcodes[index]) : undefined))
          .filter((error: any) => !!error)
          .join('. ') || '';

      const failedBarcodeIndexes: number[] = [];
      errors.trackCode?.forEach((error, index: number) => {
        if (error) failedBarcodeIndexes.push(index);
      });

      return {
        message: `${closeBoxMutation.error.message}: ${message}`,
        failedBarcodeIndexes: failedBarcodeIndexes,
      };
    }

    if (closeBoxMutation.error instanceof Error) {
      return {
        message: `${closeBoxMutation.error.message}`,
      };
    }
  }, [barcodes, closeBoxMutation.error]);

  const resetBarcodes = useCallback(() => {
    setBarcodes([]);
    closeBoxMutation.reset();
    setDuplicatedTrackCodes([]);
    setBarcodeMap({});
  }, [closeBoxMutation]);

  const removeBarcode = useCallback(
    (index: number) => {
      const barcode = barcodes.find((_, _index) => _index === index);
      setBarcodes((barcodes) => {
        return barcodes.filter((_, _index) => index !== _index);
      });

      if (barcode) {
        setDuplicatedTrackCodes((prevState) => prevState.filter((trackCode) => trackCode.barcode !== barcode.barcode));
        setBarcodeMap((prevMap) => {
          const updatedMap = { ...prevMap };
          delete updatedMap[barcode.barcode];
          return updatedMap;
        });
      }
    },
    [barcodes],
  );

  const onBarcodeSearch = useCallback(
    async (value: string) => {
      if (!value) return null;

      let trackCode = !!barcodeType ? value : value.replace(/\D/g, '');
      let trendyol = barcodeType ? '1' : '0';
      let isBarcodeAdded = barcodes.some((barcode) => barcode.barcode?.toString() === value);

      if (isBarcodeAdded) {
        return Modal.confirm({
          title: 'Diqqət',
          content: `Bu bağlama artıq əlavə edilib`,
          okText: 'Aydındır',
          okCancel: false,
          onOk: () => {
            if (barcodeInputRef.current) {
              setDisabled(false);
              barcodeInputRef.current.setValue('');
              barcodeInputRef.current?.focus();
            }
            return;
          },
        });
      }

      const result = await getDeclarationByTrackCodeRepo.execute({ trackCode, acceptance: true, trendyol });

      if (result.status !== 200) return message.error(result.response);

      const barcode = {
        barcode: result.response.trackCode,
        branch_name: result.response.branch?.name,
        flight_name: result.response.flight?.name,
      };

      if (type === 'transfer') {
        setDisabled(false);
        setBarcodes((prev) => [barcode, ...prev]);
        barcodeInputRef.current?.setValue('');
        return;
      }

      if (result.response.requiresDeclaration) {
        playSound(ErrorAudioUrls.alert);
        setBarcodes((prev) => [barcode, ...prev]);
        setBarcodeMap((prev) => ({ ...prev, [barcode.barcode]: true }));
        Modal.confirm({
          title: 'Diqqət Bəyansız Bağlama!',
          content: 'Bəyansız bağlama yeşiyə əlavə edilə bilməz.',
          okButtonProps: { hidden: true },
          okCancel: false,
        });
        await new Promise((resolve) =>
          setTimeout(() => {
            resolve('hide');
            Modal.destroyAll();
          }, 1000),
        );
        barcodeInputRef.current?.setValue('');
        barcodeInputRef.current?.focus();
        return;
      }
      if (!result.response.isYourBranch || !result.response.canAccommodate) {
        return Modal.confirm({
          title: 'Diqqət',
          content: !result.response.isYourBranch ? `Bu bağlama ${result.response.locationName} aiddir. Əlavə edilsin?` : 'Bu bağlama yerli anbarda olub. Yenidən əlavə etmək istəyinizdən əminsinizmi?',
          onOk: () => {
            setDisabled(false);
            if (!!result.response.box) {
              setDuplicatedTrackCodes((prevState) => [...prevState, barcode]);
            }
            setBarcodes((prev) => [barcode, ...prev]);
            if (barcodeInputRef.current) {
              barcodeInputRef.current.setValue('');
            }
            return;
          },
          onCancel: () => {
            setDisabled(false);
            barcodeInputRef.current?.setValue('');
            barcodeInputRef.current?.focus();
          },
        });
      }
      setBarcodes((prev) => [barcode, ...prev]);
      barcodeInputRef.current?.setValue('');
    },
    [getDeclarationByTrackCodeRepo, barcodes, type, barcodeType],
  );

  const onSelectBoxId = useCallback((value) => setBoxId(value), []);

  const onCloseBox = useCallback(() => {
    if (isRequiredDeclarationExist && !boxId) {
      onOpenBoxSelect();
      return;
    }

    const mutation = () => {
      closeBoxMutation.mutate(
        { trackCodes: barcodes.map((barcode) => barcode.barcode), containerId: boxId },
        {
          onSuccess: async () => {
            setBarcodes([]);
            setDuplicatedTrackCodes([]);
            setBarcodeMap({});
            setBoxId('');
            message.success('Əməliyyat müvəffəqiyyətlə başa çatdı.');
          },
          onError: async (error) => {
            message.error(error.message);
            setBoxId('');
          },
        },
      );
    };

    if (!!duplicatedTrackCodes.length) {
      Modal.confirm({
        title: 'Diqqət',
        content: `${duplicatedTrackCodes.length} ədəd təkrar izləmə kodu aşkarlandır. Əməliyyatı davam etməyə əminsinizmi?`,
        onOk: mutation,
      });
    } else {
      mutation();
    }
  }, [barcodes, closeBoxMutation, duplicatedTrackCodes.length, isRequiredDeclarationExist, boxId, onOpenBoxSelect]);

  const onTransferBox = useCallback(() => {
    transferBoxMutation.mutate(
      { trackCodes: barcodes.map((barcode) => barcode.barcode) },
      {
        onSuccess: async () => {
          setBarcodes([]);
          setBarcodeMap({});
          message.success('Əməliyyat müvəffəqiyyətlə başa çatdı.');
        },
        onError: async (error) => {
          message.error(error.message);
        },
      },
    );
  }, [barcodes, transferBoxMutation]);

  const onSelectBox = useCallback(
    (value) => {
      selectBoxMutation.mutate(
        { id: value },
        {
          onError: async (error) => {
            message.error(error.message);
          },
        },
      );
    },
    [selectBoxMutation],
  );

  const onTypeSwitch = useCallback(() => {
    setType((type) => (type === 'transfer' ? 'acceptance' : 'transfer'));
  }, []);

  const onBarcodeTypeSwitch = useCallback(() => {
    setBarcodeType((type) => !type);
  }, []);

  const lastBarcode = useMemo(() => barcodes[0], [barcodes]);

  useEffect(() => {
    setBarcodes(JSON.parse(localStorage.getItem('boxAcceptance.barcodes') || '[]'));
    setBarcodeMap(JSON.parse(localStorage.getItem('boxAcceptance.barcodeMap') || '{}'));
  }, []);

  useEffect(() => {
    localStorage.setItem('boxAcceptance.barcodes', JSON.stringify(barcodes));
    localStorage.setItem('boxAcceptance.barcodeMap', JSON.stringify(barcodeMap));
  }, [barcodes, barcodeMap]);

  return {
    boxes,
    myBox,
    barcodeInputRef,
    tableData,
    onBarcodeSearch,
    onTransferBox,
    transferBoxMutation,
    onSelectBox,
    onCloseBox,
    canCloseBox: !!barcodes.length,
    canClearBarcodes: !!barcodes.length,
    selectBoxMutation,
    closeBoxMutation,
    resetBarcodes,
    removeBarcode,
    closeBoxMutationError,
    type,
    onTypeSwitch,
    duplicatedTrackCodes,
    lastBarcode,
    branchId,
    setBranchId,
    disabled,
    filteredBoxes,
    branches,
    barcodeType,
    onBarcodeTypeSwitch,
    isRequiredDeclarationExist,
    onCloseBoxSelect,
    onOpenBoxSelect,
    isBoxSelectVisible,
    onSelectBoxId,
  };
};
